"use client"

import * as React from 'react'
import { useRouter } from 'next/navigation'
import Link from 'next/link'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { DataTable } from '@open-mercato/ui/backend/DataTable'
import type { ColumnDef } from '@tanstack/react-table'
import { RowActions } from '@open-mercato/ui/backend/RowActions'
import { apiCall, apiCallOrThrow } from '@open-mercato/ui/backend/utils/apiCall'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { useOrganizationScopeVersion } from '@open-mercato/shared/lib/frontend/useOrganizationScope'
import { useConfirmDialog } from '@open-mercato/ui/backend/confirm-dialog'
import { useGuardedMutation } from '@open-mercato/ui/backend/injection/useGuardedMutation'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import type { FilterDef, FilterValues } from '@open-mercato/ui/backend/FilterBar'
import { Button } from '@open-mercato/ui/primitives/button'

const VEHICLE_TYPE_OPTIONS = [
  { value: 'electric', label: 'Electric' },
  { value: 'diesel', label: 'Diesel' },
  { value: 'dual_mode', label: 'Dual Mode' },
  { value: 'shunter', label: 'Shunter' },
]

const STATUS_OPTIONS = [
  { value: 'available', label: 'Available' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'in_use', label: 'In Use' },
]

type LocomotiveRow = {
  id: string
  evn: string
  name: string
  series: string
  vehicle_type: string
  status: string
  weight_tonnes: number
  ecm_name: string
  ecm_valid_until: string | null
  created_at: string
}

type ResponsePayload = {
  items: LocomotiveRow[]
  total: number
  page: number
  totalPages: number
}

export default function LocomotivesPage() {
  const t = useT()
  const router = useRouter()
  const scopeVersion = useOrganizationScopeVersion()
  const { confirm, ConfirmDialogElement } = useConfirmDialog()
  const { runMutation } = useGuardedMutation<Record<string, unknown>>({
    contextId: 'rolling-stock-locomotives-list',
  })

  const [rows, setRows] = React.useState<LocomotiveRow[]>([])
  const [page, setPage] = React.useState(1)
  const [total, setTotal] = React.useState(0)
  const [totalPages, setTotalPages] = React.useState(1)
  const [search, setSearch] = React.useState('')
  const [filterValues, setFilterValues] = React.useState<FilterValues>({})
  const [isLoading, setIsLoading] = React.useState(true)
  const [reloadToken, setReloadToken] = React.useState(0)

  const pageSize = 50

  const queryParams = React.useMemo(() => {
    const params = new URLSearchParams()
    params.set('page', String(page))
    params.set('pageSize', String(pageSize))
    if (search) params.set('evn', search)
    if (filterValues.vehicleType) params.set('vehicleType', String(filterValues.vehicleType))
    if (filterValues.status) params.set('status', String(filterValues.status))
    if (filterValues.series) params.set('series', String(filterValues.series))
    return params.toString()
  }, [page, search, filterValues])

  React.useEffect(() => {
    let cancelled = false
    async function load() {
      setIsLoading(true)
      const fallback: ResponsePayload = { items: [], total: 0, page, totalPages: 1 }
      const call = await apiCall<ResponsePayload>(
        `/api/rolling-stock/locomotives?${queryParams}`,
        undefined,
        { fallback }
      )
      if (cancelled) return
      if (!call.ok) {
        flash(t('rolling_stock.locomotives.list.error.load', 'Failed to load locomotives'), 'error')
        setIsLoading(false)
        return
      }
      const payload = call.result ?? fallback
      setRows(payload.items)
      setTotal(payload.total)
      setTotalPages(payload.totalPages)
      setIsLoading(false)
    }
    load()
    return () => { cancelled = true }
  }, [queryParams, reloadToken, scopeVersion, t])

  const handleDelete = React.useCallback(async (row: LocomotiveRow) => {
    const confirmed = await confirm({
      title: t('rolling_stock.locomotives.delete.confirm.title', 'Delete locomotive?'),
      variant: 'destructive',
    })
    if (!confirmed) return
    try {
      await runMutation({
        operation: () => apiCallOrThrow('/api/rolling-stock/locomotives', {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: row.id }),
        }, { errorMessage: t('rolling_stock.locomotives.flash.delete_error', 'Failed to delete locomotive') }),
        context: {},
      })
      flash(t('rolling_stock.locomotives.flash.deleted', 'Locomotive deleted'), 'success')
      setReloadToken((v) => v + 1)
    } catch {
      flash(t('rolling_stock.locomotives.flash.delete_error', 'Failed to delete locomotive'), 'error')
    }
  }, [confirm, runMutation, t])

  const filters = React.useMemo<FilterDef[]>(() => [
    {
      id: 'vehicleType',
      label: t('rolling_stock.locomotives.filters.vehicleType', 'Vehicle Type'),
      type: 'select',
      options: VEHICLE_TYPE_OPTIONS,
    },
    {
      id: 'status',
      label: t('rolling_stock.locomotives.filters.status', 'Status'),
      type: 'select',
      options: STATUS_OPTIONS,
    },
    {
      id: 'series',
      label: t('rolling_stock.locomotives.filters.series', 'Series'),
      type: 'text',
    },
  ], [t])

  const columns = React.useMemo<ColumnDef<LocomotiveRow>[]>(() => [
    {
      accessorKey: 'evn',
      header: t('rolling_stock.locomotives.columns.evn', 'EVN'),
      cell: ({ row }) => (
        <Link href={`/backend/rolling-stock/locomotives/${row.original.id}`} className="font-mono text-sm">
          {row.original.evn}
        </Link>
      ),
    },
    {
      accessorKey: 'name',
      header: t('rolling_stock.locomotives.columns.name', 'Name'),
    },
    {
      accessorKey: 'series',
      header: t('rolling_stock.locomotives.columns.series', 'Series'),
    },
    {
      accessorKey: 'vehicle_type',
      header: t('rolling_stock.locomotives.columns.vehicleType', 'Type'),
      cell: ({ getValue }) => {
        const val = getValue<string>()
        return VEHICLE_TYPE_OPTIONS.find((o) => o.value === val)?.label ?? val
      },
    },
    {
      accessorKey: 'status',
      header: t('rolling_stock.locomotives.columns.status', 'Status'),
      cell: ({ getValue }) => {
        const val = getValue<string>()
        const colorMap: Record<string, string> = {
          available: 'text-green-600',
          maintenance: 'text-amber-600',
          in_use: 'text-blue-600',
        }
        const label = STATUS_OPTIONS.find((o) => o.value === val)?.label ?? val
        return <span className={colorMap[val] ?? ''}>{label}</span>
      },
    },
    {
      accessorKey: 'weight_tonnes',
      header: t('rolling_stock.locomotives.columns.weightTonnes', 'Weight (t)'),
    },
    {
      accessorKey: 'ecm_name',
      header: t('rolling_stock.locomotives.columns.ecmName', 'ECM'),
    },
    {
      accessorKey: 'ecm_valid_until',
      header: t('rolling_stock.locomotives.columns.ecmValidUntil', 'ECM Valid Until'),
      cell: ({ getValue }) => {
        const val = getValue<string | null>()
        return val ? new Date(val).toLocaleDateString() : '—'
      },
    },
  ], [t])

  return (
    <Page>
      <PageBody>
        <DataTable<LocomotiveRow>
          title={t('rolling_stock.locomotives.list.title', 'Locomotives')}
          actions={
            <Button asChild>
              <Link href="/backend/rolling-stock/locomotives/create">
                {t('rolling_stock.locomotives.list.add', 'Add Locomotive')}
              </Link>
            </Button>
          }
          columns={columns}
          data={rows}
          searchValue={search}
          onSearchChange={(v) => { setSearch(v); setPage(1) }}
          searchPlaceholder={t('rolling_stock.locomotives.list.searchPlaceholder', 'Search by EVN…')}
          filters={filters}
          filterValues={filterValues}
          onFiltersApply={(values) => { setFilterValues(values); setPage(1) }}
          onFiltersClear={() => { setFilterValues({}); setPage(1) }}
          onRowClick={(row) => router.push(`/backend/rolling-stock/locomotives/${row.id}`)}
          rowActions={(row) => (
            <RowActions
              items={[
                { id: 'edit', label: t('common.edit', 'Edit'), href: `/backend/rolling-stock/locomotives/${row.id}` },
                { id: 'delete', label: t('common.delete', 'Delete'), destructive: true, onSelect: () => handleDelete(row) },
              ]}
            />
          )}
          pagination={{ page, pageSize, total, totalPages, onPageChange: setPage }}
          isLoading={isLoading}
        />
      </PageBody>
      {ConfirmDialogElement}
    </Page>
  )
}
